/**
 * Task Control Primitives
 *
 * Shared presentation helpers for compact task metadata controls.
 *
 * Primary responsibilities:
 * - Keep task detail fields borderless and aligned
 * - Render consistent task due date labels with calendar icons
 *
 * Dependencies:
 * - Radix Separator
 * - Tabler icons
 */

"use client"

import type { FC, ReactNode } from "react"
import { IconCalendar } from "@tabler/icons-react"

import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"

export type TaskControlAppearance = "outline" | "field"

export const taskFieldTriggerClass =
  "min-h-8 min-w-0 justify-start rounded-none border-0 bg-transparent px-0 text-left font-normal shadow-none hover:bg-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"

interface TaskFieldGroupProps {
  children: ReactNode
  className?: string
}

interface TaskFieldRowProps {
  children: ReactNode
  className?: string
  icon: ReactNode
  label: string
}

interface TaskDueDateBadgeProps {
  children: ReactNode
  className?: string
  iconSize?: number
  tone?: "default" | "danger" | "muted"
  variant?: "plain" | "pill"
}

export const TaskFieldGroup: FC<TaskFieldGroupProps> = ({
  children,
  className
}) => {
  return (
    <div
      className={cn(
        "grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,18rem),1fr))] gap-3 sm:gap-4",
        className
      )}
    >
      {children}
    </div>
  )
}

export const TaskFieldRow: FC<TaskFieldRowProps> = ({
  children,
  className,
  icon,
  label
}) => {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-wrap items-center gap-2",
        className
      )}
    >
      <div className="text-muted-foreground flex min-w-28 shrink-0 items-center gap-2 text-sm font-medium">
        {icon}
        <span>{label}</span>
      </div>
      <Separator
        orientation="vertical"
        className="hidden h-5 sm:block"
      />
      <div className="min-w-0 flex-1 basis-36">
        {children}
      </div>
    </div>
  )
}

export const TaskDueDateBadge: FC<TaskDueDateBadgeProps> = ({
  children,
  className,
  iconSize = 14,
  tone = "default",
  variant = "plain"
}) => {
  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center gap-1",
        tone === "danger" && "text-red-500",
        tone === "muted" && "text-muted-foreground",
        variant === "pill" &&
          "rounded-full border border-border/70 bg-background/80 px-2.5 py-1 text-[11px] font-medium",
        className
      )}
    >
      <IconCalendar size={iconSize} className="shrink-0" />
      <span className="truncate">{children}</span>
    </span>
  )
}
